import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, Badge, Skeleton, EmptyState, Button, Input } from '../components/common';
import { emailsAPI } from '../services/api';
import { useDebounce, usePagination, useMediaQuery } from '../hooks';
import { timeAgo, statusColor, pct } from '../utils/helpers';
import toast from 'react-hot-toast';

const statusVariant = { delivered: 'green', sent: 'cyan', failed: 'red', bounced: 'amber' };

const EmailCard = ({ email, onDelete, onClick, isMobile }) => (
  <div onClick={() => onClick(email._id)} style={{
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: isMobile ? 'flex-start' : 'center', gap: 16,
    cursor: 'pointer', transition: 'background 120ms',
  }}
    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={(e) => e.currentTarget.style.background = ''}>

    {/* Open indicator dot */}
    <div style={{
      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
      background: email.openCount > 0 ? 'var(--cyan)' : 'var(--border-light)',
      boxShadow: email.openCount > 0 ? 'var(--cyan-glow)' : 'none',
    }} />

    {/* Subject + recipient */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <p style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</p>
        <Badge variant={statusVariant[email.status] || 'default'}>{email.status}</Badge>
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        → {email.recipientEmail}
        {email.tags?.length > 0 && <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>{email.tags.map((t) => `#${t}`).join(' ')}</span>}
      </p>
    </div>

    {/* Metrics */}
    <div style={{ display: 'flex', gap: 24, flexShrink: 0 }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '1rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: email.openCount > 0 ? 'var(--cyan)' : 'var(--text-muted)' }}>{email.openCount}</p>
        <p style={{ fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>opens</p>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '1rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: email.clickCount > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>{email.clickCount}</p>
        <p style={{ fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>clicks</p>
      </div>
    </div>

    {/* Time + delete */}
    <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'center' : 'flex-end', justifyContent: isMobile ? 'space-between' : 'flex-start', gap: 6, flexShrink: 0, width: isMobile ? '100%' : 'auto', marginTop: isMobile ? 8 : 0 }}>
      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{timeAgo(email.createdAt)}</span>
      <button onClick={(e) => { e.stopPropagation(); onDelete(email._id); }} style={{
        fontSize: '0.65rem', color: 'var(--text-muted)', background: 'none', border: 'none',
        cursor: 'pointer', padding: '2px 6px', borderRadius: 4,
        transition: 'all 120ms',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.background = 'var(--red-dim)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = ''; }}>
        delete
      </button>
    </div>
  </div>
);

export const EmailsPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const debouncedSearch = useDebounce(search, 350);
  const pagination = usePagination(20);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await emailsAPI.list({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
      });
      setEmails(data.data.emails);
      pagination.setTotal(data.data.pagination.total);
    } catch { toast.error('Failed to load emails'); }
    finally { setLoading(false); }
  }, [pagination.page, debouncedSearch, statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this email record?')) return;
    try {
      await emailsAPI.delete(id);
      toast.success('Deleted');
      setEmails((prev) => prev.filter((e) => e._id !== id));
      pagination.setTotal((t) => t - 1);
    } catch { toast.error('Delete failed'); }
  };

  const STATUSES = ['', 'delivered', 'sent', 'failed', 'bounced'];

  return (
    <AppLayout>
      <PageHeader
        title="Emails"
        subtitle={`${pagination.total} tracked emails`}
        actions={<Button onClick={() => navigate('/compose')} size="sm">+ Compose</Button>}
      />
      <div style={{ padding: isMobile ? '16px' : '24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : 360 }}>
            <Input
              placeholder="Search subject or recipient…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); pagination.reset(); }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? 4 : 0 }}>
            {STATUSES.map((s) => (
              <button key={s} onClick={() => { setStatusFilter(s); pagination.reset(); }} style={{
                padding: '7px 14px', borderRadius: 'var(--radius-md)',
                fontSize: '0.75rem', fontFamily: 'var(--font-mono)',
                background: statusFilter === s ? 'var(--cyan-dim)' : 'var(--bg-elevated)',
                color: statusFilter === s ? 'var(--cyan)' : 'var(--text-secondary)',
                border: `1px solid ${statusFilter === s ? 'rgba(0,225,200,0.3)' : 'var(--border)'}`,
                cursor: 'pointer', transition: 'all 150ms',
              }}>{s || 'All'}</button>
            ))}
          </div>
        </div>

        {/* List */}
        <Card style={{ overflow: 'hidden' }}>
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <Skeleton height={14} width="60%" style={{ marginBottom: 8 }} />
                <Skeleton height={11} width="30%" />
              </div>
            ))
          ) : emails.length === 0 ? (
            <EmptyState
              icon="📭"
              title="No emails found"
              description={search ? 'Try a different search term' : 'Send your first tracked email to see it here'}
              action={<Button onClick={() => navigate('/compose')} size="sm">Compose Email</Button>}
            />
          ) : (
            emails.map((email) => (
              <EmailCard key={email._id} email={email} onDelete={handleDelete} onClick={(id) => navigate(`/emails/${id}`)} isMobile={isMobile} />
            ))
          )}
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={pagination.prevPage} disabled={!pagination.hasPrev}>← Prev</Button>
            <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', padding: '0 8px' }}>
              {pagination.page} / {pagination.pages}
            </span>
            <Button variant="secondary" size="sm" onClick={pagination.nextPage} disabled={!pagination.hasNext}>Next →</Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};
